/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { LogoutSuccessAction } from '../auth/auth-actions'
import {
  AdminDeviceProvisionsAPI,
  type DeviceForProvisionCreateRequestPayload,
  type DeviceForProvisionUpdateRequestPayload
} from '../../api/admin-device-provisions-api'
import { type AppDispatch, type GetStateFunc } from '../store'
import { ActionUtil } from '../action-util'
import { ApiUtil } from '../../api/api-utils'
import { type DeviceForProvision, type DeviceForProvisionWithoutId } from './admin-device-provisions-types'
import { type FailedResponse } from '../../api/types'
import { type i18n } from 'i18next'
import { MessageTypes } from '../messages/messages-types'
import { createAction } from '@reduxjs/toolkit'
import { AddMessageAction } from '../messages/messages-actions'

/**
 * Admin device for provision operations related action types.
 */
export enum DeviceForProvisionActionType {
  // API Actions
  FETCH_DEVICES_FOR_PROVISION_SUCCESS = 'FETCH_DEVICES_FOR_PROVISION_SUCCESS',
  UPLOAD_DEVICES_FOR_PROVISION_CSV_SUCCESS = 'UPLOAD_DEVICES_FOR_PROVISION_CSV_SUCCESS',
  CREATE_DEVICE_FOR_PROVISION_SUCCESS = 'CREATE_DEVICE_FOR_PROVISION_SUCCESS',
  UPDATE_DEVICE_FOR_PROVISION_SUCCESS = 'UPDATE_DEVICE_FOR_PROVISION_SUCCESS',
  DELETE_DEVICE_FOR_PROVISION_SUCCESS = 'DELETE_DEVICE_FOR_PROVISION_SUCCESS',
  FETCH_DEVICE_PROFILES_STARTED = 'FETCH_DEVICE_PROFILES_STARTED',
  FETCH_DEVICE_PROFILES_FAILED = 'FETCH_DEVICE_PROFILES_FAILED',
  FETCH_DEVICE_PROFILES_SUCCESS = 'FETCH_DEVICE_PROFILES_SUCCESS',
  FETCH_TENANTS_STARTED = 'FETCH_TENANTS_STARTED',
  FETCH_TENANTS_FAILED = 'FETCH_TENANTS_FAILED',
  FETCH_TENANTS_SUCCESS = 'FETCH_TENANTS_SUCCESS',

  // Component states related actions
  SET_CREATE_DEVICE_FOR_PROVISION_DIALOG_STATE = 'SET_CREATE_DEVICE_FOR_PROVISION_DIALOG_STATE',
  SET_EDIT_DEVICE_FOR_PROVISION_DIALOG_STATE = 'SET_EDIT_DEVICE_FOR_PROVISION_DIALOG_STATE',
  NEW_DEVICE_FOR_PROVISION_VALUES_CHANGED = 'NEW_DEVICE_FOR_PROVISION_VALUES_CHANGED',
  EDITED_DEVICE_FOR_PROVISION_VALUES_CHANGED = 'EDITED_DEVICE_FOR_PROVISION_VALUES_CHANGED',
  SET_IMPORT_DEVICES_FOR_PROVISION_DIALOG_STATE = 'SET_IMPORT_DEVICES_FOR_PROVISION_DIALOG_STATE',
  IMPORT_DEVICES_FOR_PROVISION_PROFILE_ID_CHANGED = 'IMPORT_DEVICES_FOR_PROVISION_PROFILE_ID_CHANGED',
  IMPORT_DEVICES_FOR_PROVISION_FILE_CHANGED = 'IMPORT_DEVICES_FOR_PROVISION_FILE_CHANGED',
  SET_GENERATE_CSV_FROM_GIVEN_SERIAL_DIALOG_STATE = 'SET_GENERATE_CSV_FROM_GIVEN_SERIAL_DIALOG_STATE',
  NEW_GENERATE_CSV_FROM_SERIAL_VALUE_CHANGED = 'NEW_GENERATE_CSV_FROM_SERIAL_VALUE_CHANGED',
  SET_DEVICES_ACTIVE_PAGE = 'SET_DEVICES_ACTIVE_PAGE',
  SET_DEVICES_PAGE_SIZE = 'SET_DEVICES_PAGE_SIZE',
  SEARCH_DEVICES_FOR_PROVISION = 'SEARCH_DEVICES_FOR_PROVISION',
  SELECTED_TENANT_CHANGED = 'SELECTED_TENANT_CHANGED',
}

/* Action Creators */

// Thunk-actions //

interface ErrorPayload {
  error: string
}

// Thunks related Actions //
export const FetchDeviceProfilesAction =
    createAction(DeviceForProvisionActionType.FETCH_DEVICE_PROFILES_STARTED)
export const FetchDeviceProfilesFailedAction =
    createAction<ErrorPayload>(DeviceForProvisionActionType.FETCH_DEVICE_PROFILES_FAILED)
export const FetchDeviceProfilesSucceededAction =
    createAction<string[]>(DeviceForProvisionActionType.FETCH_DEVICE_PROFILES_SUCCESS)

export const CreateDeviceSucceededAction =
    createAction<DeviceForProvision>(DeviceForProvisionActionType.CREATE_DEVICE_FOR_PROVISION_SUCCESS)
export const DeleteDeviceSucceededAction =
    createAction<DeviceForProvision>(DeviceForProvisionActionType.DELETE_DEVICE_FOR_PROVISION_SUCCESS)

export const FetchDevicesForProvisionSucceededAction =
    createAction<DeviceForProvision[]>(DeviceForProvisionActionType.FETCH_DEVICES_FOR_PROVISION_SUCCESS)

export const UpdateDeviceSucceededAction =
    createAction<DeviceForProvision>(DeviceForProvisionActionType.UPDATE_DEVICE_FOR_PROVISION_SUCCESS)

export const UploadDevicesCsvSucceededAction =
    createAction<DeviceForProvision[]>(DeviceForProvisionActionType.UPLOAD_DEVICES_FOR_PROVISION_CSV_SUCCESS)

export const FetchTenantsAction = createAction(DeviceForProvisionActionType.FETCH_TENANTS_STARTED)
export const FetchTenantsFailedAction = createAction<ErrorPayload>(DeviceForProvisionActionType.FETCH_TENANTS_FAILED)
export const FetchTenantsSucceededAction = createAction<string[]>(DeviceForProvisionActionType.FETCH_TENANTS_SUCCESS)

// Regular Actions //

interface DialogStateChange {
  isOpen: boolean
}

export const NewDeviceForProvisionValuesChangedAction =
    createAction<DeviceForProvisionWithoutId>(DeviceForProvisionActionType.NEW_DEVICE_FOR_PROVISION_VALUES_CHANGED)

export const NewGenerateCsvFromSerialValueChangedAction = createAction<{
  serialNumber: string
}>(DeviceForProvisionActionType.NEW_GENERATE_CSV_FROM_SERIAL_VALUE_CHANGED)
export const EditedDeviceForProvisionChangedAction =
    createAction<DeviceForProvision>(DeviceForProvisionActionType.EDITED_DEVICE_FOR_PROVISION_VALUES_CHANGED)

export const ImportDevicesForProvisionFileChangedAction = createAction<{
  file: File | null
}>(DeviceForProvisionActionType.IMPORT_DEVICES_FOR_PROVISION_FILE_CHANGED)

export const ImportDevicesForProvisionProfileIdChangedAction = createAction<{
  profileId: string
}>(DeviceForProvisionActionType.IMPORT_DEVICES_FOR_PROVISION_PROFILE_ID_CHANGED)

export const SearchDevicesForProvisionAction = createAction<{
  searchQuery: string
}>(DeviceForProvisionActionType.SEARCH_DEVICES_FOR_PROVISION)

export const SetCreateDeviceDialogStateAction =
    createAction<DialogStateChange>(DeviceForProvisionActionType.SET_CREATE_DEVICE_FOR_PROVISION_DIALOG_STATE)
export const SetEditDeviceDialogStateAction = createAction<{
  isOpen: boolean
  editedDevice?: DeviceForProvision
}>(DeviceForProvisionActionType.SET_EDIT_DEVICE_FOR_PROVISION_DIALOG_STATE)

export const SetGenerateCsvFromGivenSerialDialogStateAction =
    createAction<DialogStateChange>(DeviceForProvisionActionType.SET_GENERATE_CSV_FROM_GIVEN_SERIAL_DIALOG_STATE)
export const SetImportDevicesDialogStateAction =
    createAction<DialogStateChange>(DeviceForProvisionActionType.SET_IMPORT_DEVICES_FOR_PROVISION_DIALOG_STATE)

export const SetDevicesActivePageAction = createAction<{
  pageNumber: number
}>(DeviceForProvisionActionType.SET_DEVICES_ACTIVE_PAGE)

export const SetDevicesPageSizeAction = createAction<{
  pageSize: number
}>(DeviceForProvisionActionType.SET_DEVICES_PAGE_SIZE)

export const SelectedTenantChangedAction = createAction<string>(DeviceForProvisionActionType.SELECTED_TENANT_CHANGED)

/* Thunk Actions */

export function createDeviceForProvision(payload: DeviceForProvisionCreateRequestPayload) {
  return async (dispatch: AppDispatch, getState: GetStateFunc): Promise<void> => {
    const token = ActionUtil.getToken(getState, 'createDeviceForProvision')
    if (!token) {
      return
    }

    // note: there could be action that indicates loading state (but currently isn't)
    const createRes = await AdminDeviceProvisionsAPI.createDeviceForProvision(payload, token)
    if (createRes.succeeded) {
      dispatch(CreateDeviceSucceededAction(createRes.payload.device))
    } else if (ApiUtil.shouldForceLogout(createRes)) {
      initiateForceLogout(createRes, dispatch)
    } else {
      const errMsg = ApiUtil.failedResponseToDisplayString(createRes)
      console.warn('Error received on device for provision creation:', errMsg)

      notifyErrorByKey(createRes.errorKey ?? 'deviceForProvision.createFailedDefaultError', dispatch)
    }
  }
}

export function deleteDeviceForProvision(id: string) {
  return async (dispatch: AppDispatch, getState: GetStateFunc) => {
    const token = ActionUtil.getToken(getState, 'deleteDeviceForProvision')
    if (!token) {
      return
    }

    // note: there could be action that indicates loading state (but currently isn't)
    const deleteRes = await AdminDeviceProvisionsAPI.deleteDeviceForProvision(id, token)
    if (deleteRes.succeeded) {
      dispatch(DeleteDeviceSucceededAction(deleteRes.payload.device))
    } else if (ApiUtil.shouldForceLogout(deleteRes)) {
      initiateForceLogout(deleteRes, dispatch)
    } else {
      const errMsg = ApiUtil.failedResponseToDisplayString(deleteRes)
      console.warn('Error received on device for provision deletion:', errMsg)

      notifyErrorByKey(deleteRes.errorKey ?? 'deviceForProvision.deleteFailedDefaultError', dispatch)
    }
  }
}

export function fetchDeviceProfiles() {
  return async (dispatch: AppDispatch, getState: GetStateFunc) => {
    const token = ActionUtil.getToken(getState, 'fetchDeviceProfiles')
    if (!token) {
      return
    }

    dispatch({ type: DeviceForProvisionActionType.FETCH_DEVICE_PROFILES_STARTED })
    const fetchRes = await AdminDeviceProvisionsAPI.getDeviceProfiles(token)
    if (fetchRes.succeeded) {
      const profileIds = fetchRes.payload.profiles.map(it => it.name)
      dispatch(FetchDeviceProfilesSucceededAction(profileIds))
    } else if (ApiUtil.shouldForceLogout(fetchRes)) {
      initiateForceLogout(fetchRes, dispatch)
    } else {
      const errMsg = ApiUtil.failedResponseToDisplayString(fetchRes)
      console.warn('Error received on fetching device profiles:', errMsg)
      notifyErrorByKey(fetchRes.errorKey ?? 'deviceForProvision.getDeviceProfilesFailedDefaultError', dispatch)
      dispatch(FetchDeviceProfilesFailedAction({ error: errMsg }))
    }
  }
}

export function fetchDevicesForProvision() {
  return async (dispatch: AppDispatch, getState: GetStateFunc) => {
    const token = ActionUtil.getToken(getState, 'fetchDevicesForProvision')
    if (!token) {
      return
    }

    const { selectedTenant } = getState().adminDeviceProvisions

    // note: there could be action that indicates loading state (but currently isn't)
    const fetchRes = await AdminDeviceProvisionsAPI.getDevicesForProvision(token, selectedTenant)
    if (fetchRes.succeeded) {
      dispatch(FetchDevicesForProvisionSucceededAction(fetchRes.payload.devices))
    } else if (ApiUtil.shouldForceLogout(fetchRes)) {
      initiateForceLogout(fetchRes, dispatch)
    } else {
      const errMsg = ApiUtil.failedResponseToDisplayString(fetchRes)
      console.warn('Error received on fetching all devices for provision:', errMsg)

      notifyErrorByKey(fetchRes.errorKey ?? 'deviceForProvision.getDevicesFailedDefaultError', dispatch)
    }
  }
}

export function fetchTenants() {
  return async (dispatch: AppDispatch, getState: GetStateFunc) => {
    const token = ActionUtil.getToken(getState, 'fetchTenants')
    if (!token) {
      return
    }

    dispatch(FetchTenantsAction())

    // note: there could be action that indicates loading state (but currently isn't)
    const fetchRes = await AdminDeviceProvisionsAPI.getTenants(token)
    if (fetchRes.succeeded) {
      const tenantNames = fetchRes.payload.tenants.map(it => it.name)
      dispatch(FetchTenantsSucceededAction(tenantNames))
    } else if (ApiUtil.shouldForceLogout(fetchRes)) {
      dispatch(FetchTenantsFailedAction({ error: 'Unauthorized or forbidden access' }))
      initiateForceLogout(fetchRes, dispatch)
    } else {
      const errMsg = ApiUtil.failedResponseToDisplayString(fetchRes)
      dispatch(FetchTenantsFailedAction({ error: errMsg }))
      console.warn('Error received on fetching tenants:', errMsg)
      notifyErrorByKey(fetchRes.errorKey ?? 'deviceForProvision.getTenantsFailedDefaultError', dispatch)
    }
  }
}

export function updateDeviceForProvision(id: string, payload: DeviceForProvisionUpdateRequestPayload) {
  return async (dispatch: AppDispatch, getState: GetStateFunc) => {
    const token = ActionUtil.getToken(getState, 'updateDeviceForProvision')
    if (!token) {
      return
    }

    // note: there could be action that indicates loading state (but currently isn't)
    const updateRes = await AdminDeviceProvisionsAPI.updateDeviceForProvision(id, payload, token)
    if (updateRes.succeeded) {
      dispatch(UpdateDeviceSucceededAction(updateRes.payload.device))
    } else if (ApiUtil.shouldForceLogout(updateRes)) {
      initiateForceLogout(updateRes, dispatch)
    } else {
      const errMsg = ApiUtil.failedResponseToDisplayString(updateRes)
      console.warn('Error received on device for provision update:', errMsg)

      notifyErrorByKey(updateRes.errorKey ?? 'deviceForProvision.updateFailedDefaultError', dispatch)
    }
  }
}

export function uploadDevicesForProvisionCsv(formData: FormData, t: i18n['t']) {
  return async (dispatch: AppDispatch, getState: GetStateFunc) => {
    const token = ActionUtil.getToken(getState, 'uploadDevicesForProvisionCsv')
    if (!token) {
      return
    }

    // note: there could be action that indicates loading state (but currently isn't)
    const importRes = await AdminDeviceProvisionsAPI.uploadDeviceForProvisionCsv(formData, token)
    if (importRes.succeeded) {
      if (importRes.payload.error != null && importRes.payload.devices.length === 0) {
        // full failure: Import error
        const errPrefix = t('deviceForProvision.importFromCsvError')
        const errMsg = `${errPrefix}:\n${importRes.payload.error}`

        console.warn('Error received on device for provision CSV import:', importRes.payload.error)
        notifyLiteralMessage(errMsg, MessageTypes.error, dispatch)
      } else {
        const successPrefix = t('deviceForProvision.importFromCsvSuccessPrefix')

        if (importRes.payload.error != null) {
          // partial failure
          const errorPrefix = t('deviceForProvision.importFromCsvErrorPrefix')
          const report = `${successPrefix}: ${importRes.payload.devices.length}, ` +
                        `${errorPrefix}: \n${importRes.payload.error}`
          notifyLiteralMessage(report, MessageTypes.warning, dispatch)
        } else {
          // success
          const report = `${successPrefix}: ${importRes.payload.devices.length}`
          notifyLiteralMessage(report, MessageTypes.success, dispatch)
        }

        dispatch(UploadDevicesCsvSucceededAction(importRes.payload.devices))
      }
    } else if (ApiUtil.shouldForceLogout(importRes)) {
      initiateForceLogout(importRes, dispatch)
    } else {
      const errMsg = ApiUtil.failedResponseToDisplayString(importRes)
      console.warn('Error received on device for provision CSV upload:', errMsg)

      notifyErrorByKey(importRes.errorKey ?? 'deviceForProvision.uploadFailedDefaultError', dispatch)
    }
  }
}

/* Helper Functions */

function initiateForceLogout(errorRes: FailedResponse, dispatch: AppDispatch): void {
  console.warn('Initiating force logout due to error response:', errorRes)
  notifyErrorByKey('unauthorized', dispatch)
  dispatch(LogoutSuccessAction())
}

/**
 * Notify (dispatch) error by 'errorKey'.
 * @param errorKey errorKey (unique identifier for the error case).
 * @param dispatch App dispatcher.
 */
function notifyErrorByKey(errorKey: string, dispatch: AppDispatch): void {
  dispatch(AddMessageAction(errorKey, MessageTypes.error))
}

/**
 * Notify (dispatch) error with literal ('as is' without translating) message.
 * @param message Error message.
 * @param type Message type.
 * @param dispatch App dispatcher.
 */
function notifyLiteralMessage(message: string, type: MessageTypes, dispatch: AppDispatch): void {
  dispatch(AddMessageAction(message, type, true))
}
